import type { ContrastIssue } from "@gatiq/shared";
import { CheckCircle2 } from "lucide-react";
import { Alert, Button } from "@/ui";

/**
 * Guardrail #1 (D-02): shows every WCAG AA failure the current draft would
 * introduce, each with the measured ratio, the minimum required, and a
 * one-click fix. `BrandingEditor` disables Save while this list is non-empty.
 */
export function ContrastWarnings({
  issues,
  onUseSuggestion,
}: {
  issues: ContrastIssue[];
  onUseSuggestion: (tokenKey: string, hex: string) => void;
}) {
  // Passing is the normal state, so it gets a status line rather than a
  // full success panel. Failures keep the panel — they block Save.
  if (issues.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-success">
        <CheckCircle2 aria-hidden="true" className="size-4 shrink-0" />
        Every text colour clears WCAG AA.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {issues.map((issue) => (
        <Alert key={`${issue.pair[0]}-${issue.pair[1]}`} kind="danger" title="Text will not be readable">
          <p>
            {issue.label} — contrast {issue.ratio.toFixed(2)}:1 (minimum {issue.required}:1)
          </p>
          {issue.suggestion ? (
            <div className="mt-2">
              <Button
                onClick={() => onUseSuggestion(issue.pair[0], issue.suggestion as string)}
                size="sm"
                type="button"
                variant="secondary"
              >
                Use {issue.suggestion} instead
              </Button>
            </div>
          ) : null}
        </Alert>
      ))}
    </div>
  );
}
